import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Star, MapPin, Shield, Package, ChevronRight, Search, SlidersHorizontal, Tag, Flame, Award, MessageSquare } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import StoreMessageBox from '../components/StoreMessageBox';

import { productUrl } from '../lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const formatPrice = (price) => {
  if (!price) return '₺0,00';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(price);
};

const StarRating = ({ rating, size = 'h-4 w-4' }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} ${i < Math.round(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
    ))}
  </div>
);

const ProductCard = ({ product, dealPrice }) => {
  const price = dealPrice || product.display_price || product.price || (product.base_price ? product.base_price * 1.4 : 0);
  const originalPrice = dealPrice ? (product.display_price || product.price) : null;
  const img = product.image_url || product.images?.[0] || '';
  
  return (
    <Link to={productUrl(product)} className="group bg-white rounded-xl border hover:shadow-lg transition-all duration-200 overflow-hidden" data-testid={`store-product-${product.id}`}>
      <div className="aspect-square bg-slate-100 relative overflow-hidden">
        {img ? (
          <img src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`} alt={product.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400"><Package className="h-12 w-12" /></div>
        )}
        {dealPrice && <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-lg">{product.discount_percent || Math.round((1 - dealPrice / (originalPrice || 1)) * 100)}% indirim</div>}
      </div>
      <div className="p-3">
        <p className="text-xs text-slate-500 mb-1">{product.brand}</p>
        <h3 className="text-sm font-medium text-slate-900 line-clamp-2 mb-2 min-h-[2.5rem]">{product.name}</h3>
        <div className="flex items-end gap-2">
          <span className="text-lg font-bold text-orange-600">{formatPrice(price)}</span>
          {originalPrice && originalPrice > dealPrice && (
            <span className="text-xs text-slate-400 line-through">{formatPrice(originalPrice)}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

const StorePage = () => {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [deals, setDeals] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [productPage, setProductPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchStoreData();
  }, [slug]);

  useEffect(() => {
    if (activeTab === 'products' || activeTab === 'home') {
      fetchProducts();
    }
  }, [activeTab, productPage, sortBy, selectedCategory, searchQuery]);

  const fetchStoreData = async () => {
    try {
      const [storeRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/store/${slug}`),
        axios.get(`${API}/store/${slug}/reviews?limit=5`)
      ]);
      setStore(storeRes.data.store);
      setFeatured(storeRes.data.featured_products);
      setDeals(storeRes.data.deal_products);
      setCampaigns(storeRes.data.campaigns);
      setCategories(storeRes.data.store_categories);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const params = { page: productPage, limit: 24, sort: sortBy };
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/store/${slug}/products`, { params });
      setProducts(res.data.products);
      setTotalProducts(res.data.total);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" /></div>;
  if (!store) return <div className="text-center py-20"><h2 className="text-xl text-slate-600">Magaza bulunamadi</h2></div>;

  const themeColor = store.theme_color || '#f97316';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Banner */}
      <div className="relative h-48 md:h-64" style={{ backgroundColor: themeColor }} data-testid="store-banner">
        {store.banner_url && (
          <img src={store.banner_url.startsWith('http') ? store.banner_url : `${BACKEND_URL}${store.banner_url}`} alt="Banner" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Store Info */}
      <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10" data-testid="store-info">
        <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col md:flex-row items-start gap-6">
          {/* Logo */}
          <div className="w-24 h-24 rounded-2xl border-4 border-white shadow-md bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {store.logo_url ? (
              <img src={store.logo_url.startsWith('http') ? store.logo_url : `${BACKEND_URL}${store.logo_url}`} alt={store.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-3xl font-bold" style={{ color: themeColor }}>{store.name.charAt(0).toUpperCase()}</span>
            )}
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-900">{store.name}</h1>
              {store.is_verified && <Shield className="h-5 w-5 text-blue-500" />}
            </div>

            
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-1">
                <StarRating rating={store.rating} size="h-4 w-4" />
                <span className="font-medium text-slate-700 ml-1">{store.rating.toFixed(1)}</span>
                <span>({store.rating_count} degerlendirme)</span>
              </div>
              <div className="flex items-center gap-1"><Package className="h-4 w-4" /><span>{store.total_products} urun</span></div>
              <div className="flex items-center gap-1"><Award className="h-4 w-4" /><span>{store.total_sales} satis</span></div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <StoreMessageBox storeId={store.id} storeName={store.name} />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="store-tabs">
          {[
            { id: 'home', label: 'Ana Sayfa' },
            { id: 'products', label: 'Tum Urunler' },
            { id: 'reviews', label: `Degerlendirmeler (${store.rating_count})` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setProductPage(1); }}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                activeTab === tab.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* HOME TAB */}
        {activeTab === 'home' && (
          <div className="space-y-8">
            {/* Campaigns */}
            {campaigns.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Tag className="h-5 w-5 text-orange-500" /> Kampanyalar</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {campaigns.map(c => (
                    <div key={c.id} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white">
                      <h3 className="text-xl font-bold mb-1">{c.title}</h3>
                      <p className="text-white/80 text-sm">{c.description}</p>
                      {c.discount_percent > 0 && <Badge className="mt-3 bg-white/20 text-white text-lg">%{c.discount_percent} indirim</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deal Products */}
            {deals.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Flame className="h-5 w-5 text-red-500" /> Firsat Urunleri</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {deals.map(p => <ProductCard key={p.id} product={p} dealPrice={p.deal_price} />)}
                </div>
              </div>
            )}

            {/* Featured Products */}
            {featured.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Award className="h-5 w-5 text-orange-500" /> One Cikan Urunler</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {featured.map(p => <ProductCard key={p.id} product={p} />)}
                </div>
              </div>
            )}

            {/* Categories */}
            {categories.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-4">Kategoriler</h2>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat, idx) => (
                    <button key={idx} onClick={() => { setSelectedCategory(cat); setActiveTab('products'); }} className="px-4 py-2 bg-white border rounded-full text-sm hover:border-orange-400 hover:text-orange-600 transition-colors">
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Products */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Urunler</h2>
                <button onClick={() => setActiveTab('products')} className="text-orange-500 text-sm hover:underline flex items-center gap-1">Tumunu Gor <ChevronRight className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {products.slice(0, 12).map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (
          <div>
            {/* Search + Sort */}
            <div className="flex flex-col md:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setProductPage(1); }}
                  placeholder="Magazada ara..."
                  className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                  data-testid="store-search"
                />
              </div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-4 py-2.5 border rounded-xl text-sm bg-white" data-testid="store-sort">
                <option value="newest">En Yeni</option>
                <option value="price_asc">Fiyat (Dusuk-Yuksek)</option>
                <option value="price_desc">Fiyat (Yuksek-Dusuk)</option>
                <option value="popular">En Cok Satan</option>
                <option value="rating">En Yuksek Puan</option>
              </select>
            </div>

            {/* Category Filter */}
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => { setSelectedCategory(''); setProductPage(1); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!selectedCategory ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Tumu</button>
                {categories.map((cat, idx) => (
                  <button key={idx} onClick={() => { setSelectedCategory(cat); setProductPage(1); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedCategory === cat ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{cat}</button>
                ))}
              </div>
            )}

            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
            {products.length === 0 && <p className="text-center text-slate-500 py-10">Urun bulunamadi</p>}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setProductPage(p)} className={`w-10 h-10 rounded-lg text-sm font-medium ${p === productPage ? 'bg-orange-500 text-white' : 'bg-white border text-slate-600 hover:border-orange-300'}`}>{p}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && <StoreReviews slug={slug} store={store} />}
      </div>
    </div>
  );
};

const StoreReviews = ({ slug, store }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchReviews();
  }, [page]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/store/${slug}/reviews?page=${page}&limit=10`);
      setReviews(res.data.reviews);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="bg-white rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-4xl font-bold text-slate-900">{store.rating.toFixed(1)}</p>
            <StarRating rating={store.rating} size="h-5 w-5" />
            <p className="text-sm text-slate-500 mt-1">{store.rating_count} degerlendirme</p>
          </div>
        </div>
      </div>

      {reviews.length === 0 && <p className="text-center text-slate-500 py-10">Henuz degerlendirme yok</p>}

      <div className="space-y-4">
        {reviews.map((r, idx) => (
          <div key={idx} className="bg-white rounded-xl p-4 border">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm">{(r.user_name || 'A').charAt(0)}</div>
              <div>
                <p className="font-medium text-sm text-slate-900">{r.user_name || 'Anonim'}</p>
                <StarRating rating={r.rating} size="h-3 w-3" />
              </div>
              <span className="ml-auto text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString('tr-TR')}</span>
            </div>
            {r.comment && <p className="text-sm text-slate-600">{r.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StorePage;
